import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

// Contexts
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import {
  ArticlesMenuItem,
  EmptyArticlesState,
} from "./components";
import CommonHeader from "../../components/common/CommonHeader";
import PremiumDialog from "../../components/common/PremiumDialog";
import ProgressModal from "../../components/studyquestion/ProgressModal";

// Constants
import { GET_ARTICLE_MENU, BUNDLE_ARTICLES, GET_APP_DOMAIN_LIST, IS_TEST_MODE } from "../../../app_constant";
import { STRING, STRINGS } from "../../constant";
import { 
  faBook, 
  faCheckCircle, 
  faBookmark, 
  faChartBar,
  faLock
} from "@fortawesome/free-solid-svg-icons";
import StatsGrid from "../../components/common/StatsGrid";

let _articlesTracked = false;

const Articles = () => {
  const navigate = useNavigate();
  const { selectedCertificate } = useContext(BundleContext);
  const { 
    articlesList,
    articles,
    profile,
    isSubscribe,
    getArticleReadSummary,
    getArticleBookmarks,
  } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { track, trackUserAction } = useAnalytics();

  // State management
  const [expandedMenus, setExpandedMenus] = useState(['by_topic']);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedMainMenu, setSelectedMainMenu] = useState(null);
  const [selectedBookmarkTopic, setSelectedBookmarkTopic] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalType, setProgressModalType] = useState('article');
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [isProgressTesting, setProgressTesting] = useState(false);

  let ARTICLES_MENU = GET_ARTICLE_MENU(selectedCertificate);
  let ARTICLES = BUNDLE_ARTICLES[selectedCertificate] || [];

  // Filter out "By Topics (Free Plan)" menu item completely for all users
  ARTICLES_MENU = ARTICLES_MENU.filter(item => item.key !== 'by_topic_free_model');

  // Load article data on component mount (fallback for initial load)
  useEffect(() => {
    const loadArticleData = async () => {
      try {
        await getArticleReadSummary();
        await getArticleBookmarks();
      } catch (error) {
        console.error('Error loading article data:', error);
      }
    };

    if (!articles.bookmarks.length) {
      loadArticleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track articles list view (once per page load)
  useEffect(() => {
    if (_articlesTracked) return;
    _articlesTracked = true;
    track('articles_menu_viewed', {
      articles_count: ARTICLES.length,
      articles_read: articles.readSummary?.total_articles_read || 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate total free articles for non-subscribed users
  let totalFreeArticles = 0;
  if (!isSubscribe) {
    const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
    APP_DOMAIN_LIST.forEach(domain => {
      totalFreeArticles += domain.num_articles || 0;
    });
  }

  // Stats for the stats grid
  const stats = [
    {
      label: "Total Articles",
      stat: ARTICLES.length,
      icon: faBook,
      iconType: 'fontawesome',
      color: theme?.primary?.blue?.[500],
    },
    {
      label: "Bookmarks",
      stat: articles.bookmarks.length || 0,
      icon: faBookmark,
      iconType: 'fontawesome',
      color: theme?.primary?.orange?.[500],
    },
  ];

  // Add Free Articles stat for non-subscribed users
  if (!isSubscribe && totalFreeArticles > 0) {
    stats.push({
      label: "Free Articles",
      stat: totalFreeArticles,
      icon: faLock,
      iconType: 'fontawesome',
      color: theme?.primary?.green?.[500],
    });
  }

  // Get article count for menu items
  const getArticleCount = (menuType, item) => {
    if (menuType === "bookmark") {
      const bookmarks = articles.bookmarks;
      if (item.domain_name === "All Bookmarks") {
        return ARTICLES?.filter(a => bookmarks.includes(a.id)).length || 0;
      }
      return ARTICLES
        ?.filter(a => bookmarks.includes(a.id))
        .filter(a => a.topic_id === item.id).length || 0;
    }
    return ARTICLES?.filter(a => a.topic_id === item.id).length || 0;
  };

  // Get read count for a topic
  const getTopicReadCount = (topicId) => {
    const topicStats = articles.readSummary?.articles_by_topic?.[topicId];
    return topicStats?.read_count || 0;
  };

  // Check if topic has progress
  const hasProgress = (setId) => {
    const progress = profile?.articles_progress || [];
    const setIdString = String(setId);
    return progress.some(p => String(p.articles_set_id) === setIdString);
  };

  // Check if All Articles (Testing) has progress
  const hasTestingProgress = () => {
    if (!IS_TEST_MODE) return false;
    const progress = profile?.articles_progress || [];
    const setProgress = progress.filter((obj) => {
      return obj.articles_set_id === 'all_articles_testing';
    });
    return setProgress && setProgress.length > 0 && ARTICLES.length > 0 &&
           ARTICLES[0].id !== setProgress[0].last_seen_article_id;
  };

  // Handle topic selection
  const handleTopicClick = (item, menu) => {
    // For non-subscribed users clicking on "By Topics", allow access to free articles
    // Don't redirect to chooseplan - they can access free articles
    if (item.requires_subscription && !isSubscribe && menu.free_model !== 1 && menu.key !== "by_topic") {
      navigate('/chooseplan', {
        state: { canSkip: false, source: 'articles_menu' }
      });
      return;
    }

    const setId = menu.key === "bookmark" 
      ? (item.domain_name === "All Bookmarks" ? "bookmark" : `bookmark_${item.id}`)
      : item.id + 2;

    // Check for existing progress
    if (hasProgress(setId)) {
      setSelectedTopic(item);
      setSelectedMainMenu(menu);
      setProgressModalType(menu.key === "bookmark" ? "bookmark" : "article");
      setShowProgressModal(true);
    } else {
      navigateToArticles(item, menu, false);
    }
  };

  // Navigate to articles
  const navigateToArticles = (item, menu, resume = false) => {
    let articlesList;
    let setId;

    if (menu.key === "bookmark") {
      const bookmarks = articles?.bookmarks || [];
      articlesList = item.domain_name === "All Bookmarks"
        ? ARTICLES?.filter(a => bookmarks.includes(a.id))
        : ARTICLES?.filter(a => bookmarks.includes(a.id) && a.topic_id === item.id);
      setId = item.domain_name === "All Bookmarks" ? "bookmark" : `bookmark_${item.id}`;
    } else {
      articlesList = ARTICLES?.filter(a => a.topic_id === item.id);
      setId = item.id + 2;
      
      // Apply free model limit for non-subscribed users clicking on "By Topics"
      if (!isSubscribe && menu.key === "by_topic" && item.num_articles) {
        articlesList = articlesList.slice(0, item.num_articles);
      }
    }

    // Sort articles by article_id within topic
    articlesList = articlesList.sort((a, b) => a.id - b.id);

    const navigationState = {
      set_id: setId,
      source: "articles_menu",
      articles: articlesList,
      topic_name: item.domain_name
    };

    // Add last index if resuming
    if (resume) {
      const progress = profile?.articles_progress || [];
      // Convert setId to string for consistent comparison
      const setIdString = String(setId);
      const setProgress = progress.find(p => String(p.articles_set_id) === setIdString);
      if (setProgress) {
        const lastIndex = articlesList.findIndex(a => a.id === setProgress.last_seen_article_id);
        navigationState.lastIndex = lastIndex === -1 ? 0 : lastIndex;
      }
    }
    
    // Track article navigation
    trackUserAction('article_category_accessed', {
      category_name: item?.domain_name || 'Unknown',
      article_count: navigationState?.articles?.length || 0,
      is_resume: resume
    });

    navigate('/articles/article', { state: navigationState });
  };

  // Handle special menu items (Random, Bookmarks root, Testing mode)
  const handleSpecialMenuItem = (item, index) => {
    if (index === 0) { // Bookmarks root
      const bookmarks = articles?.bookmarks || [];
      const articlesList = ARTICLES?.filter(a => bookmarks.includes(a.id));
      
      if (hasProgress("bookmark")) {
        setSelectedTopic({ domain_name: "All Bookmarks" });
        setSelectedMainMenu(item);
        setProgressModalType("bookmark");
        setShowProgressModal(true);
      } else {
        trackUserAction('article_category_accessed', {
          category_name: "All Bookmarks",
          article_count: articlesList.length,
          is_bookmarks: true
        });
        navigate('/articles/article', {
          state: { set_id: "bookmark", articles: articlesList, topic_name: "All Bookmarks" }
        });
      }
    } else if (index === 1) { // Random articles
      const articlesList = _.shuffle(ARTICLES).slice(0, 10);
      trackUserAction('article_category_accessed', {
        category_name: "Random Articles",
        article_count: articlesList.length,
        is_random: true
      });
      navigate('/articles/article', {
        state: { title: item.title, articles: articlesList, topic_name: "Random Articles" }
      });
    } else if (item.key === 'all_articles_testing' && IS_TEST_MODE) {
      // Check for existing progress and show resume modal if needed
      if (hasTestingProgress()) {
        // Show resume popup
        setProgressTesting(true);
      } else {
        // No progress or at beginning, navigate directly
        navigate('/articles/article', {
          state: {
            set_id: 'all_articles_testing',
            articles: ARTICLES,
            topic_name: 'All Articles (Testing)',
            testing_mode: true
          }
        });
      }
    }
  };

  // Handle progress modal action
  const handleProgressAction = (startOver) => {
    setShowProgressModal(false);
    navigateToArticles(selectedTopic, selectedMainMenu, !startOver);
    setSelectedTopic(null);
    setSelectedMainMenu(null);
  };

  // Handle testing mode progress action
  const handleTestingProgressAction = (startOver) => {
    if (!IS_TEST_MODE) return;
    
    setProgressTesting(false);
    const progress = profile?.articles_progress || [];
    const setProgress = progress.filter((obj) => {
      return obj.articles_set_id === 'all_articles_testing';
    });
    const articlesList = ARTICLES;
    
    if (startOver) {
      // Start from beginning
      navigate('/articles/article', {
        state: {
          set_id: 'all_articles_testing',
          articles: articlesList,
          topic_name: 'All Articles (Testing)',
          testing_mode: true
        }
      });
    } else {
      // Resume from where stopped
      if (setProgress.length > 0) {
        const articleIndex = articlesList.findIndex((obj) => obj.id === setProgress[0].last_seen_article_id);
        navigate('/articles/article', {
          state: {
            set_id: 'all_articles_testing',
            articles: articlesList,
            lastIndex: articleIndex === -1 ? 0 : articleIndex,
            topic_name: 'All Articles (Testing)',
            testing_mode: true
          }
        });
      }
    }
  };

  // Toggle menu expansion
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => 
      prev.includes(menuKey) 
        ? prev.filter(key => key !== menuKey)
        : [...prev, menuKey]
    );
  };

  // Handle premium upgrade
  const handleUpgrade = () => {
    navigate('/chooseplan');
  };

  // Handle locked item click
  const handleLockedItemClick = (item) => {
    setShowPremiumDialog(true);
  };

  // Don't show articles if no articles available for this certificate
  if (ARTICLES.length === 0) {
    return (
      <div className="min-h-screen transition-colors duration-300" 
           style={{ background: theme?.background?.main }}>
        <CommonHeader
          title="Articles"
          icon={faBook}
          subtitle="Study articles and comprehensive guides"
          quote="Knowledge is power. Reading is the key to unlocking it."
          onBack={() => navigate("/home")}
          theme={theme}
          showBackButton={true}
        />
        <EmptyArticlesState theme={theme} />
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        <CommonHeader
          title="Articles"
          icon={faBook}
          subtitle="Study articles and comprehensive guides"
          quote="Knowledge is power. Reading is the key to unlocking it."
          onBack={() => navigate("/home")}
          theme={theme}
          showBackButton={true}
        />
        <div className="pb-4">
          <StatsGrid stats={stats} theme={theme} />
        </div>
        
        {/* Menu Items */}
        <div className="space-y-4">
          {ARTICLES_MENU.map((menu, index) => (
            <ArticlesMenuItem
              key={menu.key}
              menu={menu}
              isExpanded={expandedMenus.includes(menu.key)}
              onToggle={() => toggleMenu(menu.key)}
              onItemClick={(item) => handleTopicClick(item, menu)}
              onMenuClick={() => handleSpecialMenuItem(menu, index)}
              getArticleCount={(item) => getArticleCount(menu.key, item)}
              getTopicReadCount={getTopicReadCount}
              isSubscribed={isSubscribe}
              theme={theme}
              onLockedItemClick={handleLockedItemClick}
              articlesList={ARTICLES}
            />
          ))}
        </div>
        
        {/* Empty State */}
        {ARTICLES_MENU.length === 0 && (
          <EmptyArticlesState theme={theme} />
        )}
      </div>
      
      {/* Progress Modal */}
      <ProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        onStartOver={() => handleProgressAction(true)}
        onResume={() => handleProgressAction(false)}
        title={STRINGS.articles || "Articles"}
        message="You have progress on this article set. Would you like to resume or start over?"
        theme={theme}
      />

      {/* Testing Mode Progress Modal */}
      {IS_TEST_MODE && (
        <ProgressModal
          isOpen={isProgressTesting}
          onClose={() => setProgressTesting(false)}
          onStartOver={() => handleTestingProgressAction(true)}
          onResume={() => handleTestingProgressAction(false)}
          title="All Articles (Testing)"
          message="You have progress on this article set. Would you like to resume or start over?"
          theme={theme}
        />
      )}

      {/* Premium Dialog */}
      <PremiumDialog
        isOpen={showPremiumDialog}
        onClose={() => setShowPremiumDialog(false)}
        onUpgrade={handleUpgrade}
        theme={theme}
      />
    </div>
  );
};

export default Articles; 